/**
 * Live-price subscriptions for the data grid: which websocket messages the rows on screen need, and
 * what to send as the rows change. Pure functions, unit-tested in `e2e/unit/liveSubscriptions.spec.ts`;
 * the effect in `DataGrid.tsx` does the sending.
 *
 * A row's producer returns one or more messages for the same instrument, for example a `subscribe` and a
 * `last_trades_history_w_ticker` request with the same identifier, so messages are keyed by kind and instrument.
 * Nothing here builds an unsubscribe: see `planSends`.
 */

export type LiveMessage = { ISIN?: string; identifier?: string; type?: string; [key: string]: unknown };

/** A message with no `type` is a subscription: that is how the currencies widget has always sent them. */
export const isSubscription = (message: LiveMessage): boolean => !message.type || message.type === "subscribe";

/** A message's key: its kind and instrument. Messages without an ISIN or identifier are never sent, as before. */
export const messageKey = (message: LiveMessage | null | undefined): string | null => {
    const instrument = message?.ISIN || message?.identifier;
    if (!instrument) return null;
    return `${isSubscription(message) ? "subscribe" : message.type}|${instrument}`;
};

/**
 * Every message the rows produce, keyed; the first row wins when two rows share one.
 * One subscription per instrument means a row leaving does not silence a row that stays.
 */
export function collectMessages(rows: readonly unknown[], message: (row: any) => LiveMessage[] | null | undefined): Map<string, LiveMessage> {
    const out = new Map<string, LiveMessage>();
    for (const row of rows) {
        for (const m of message(row) || []) {
            const key = messageKey(m);
            if (key && !out.has(key)) out.set(key, m);
        }
    }
    return out;
}

/** What the grid has sent on one socket: the subscriptions, and the requests whose rows are on screen. */
export type LiveState = { subscribed: Set<string>; requested: Set<string> };

export const emptyLiveState = (): LiveState => ({ subscribed: new Set(), requested: new Set() });

/**
 * The frames to send for the rows' messages `next`, and the state after sending them.
 *
 * The grid never unsubscribes. The server keeps one subscription per instrument per connection, so a grid
 * unsubscribe also ends the feed of every other component on the page that watches the instrument: on /currencies
 * the live chart beside the grid stopped updating when a sort moved its pair off the grid's page (#1285). A
 * subscription is therefore sent once per socket and kept, as the grid has always effectively done. A request that
 * is not a subscription (a history request) is sent again when its row re-enters, because the cell that renders
 * the reply is new.
 */
export function planSends(next: Map<string, LiveMessage>, state: LiveState): { frames: LiveMessage[]; state: LiveState } {
    const subscribed = new Set(state.subscribed);
    const requested = new Set<string>();
    const frames: LiveMessage[] = [];
    next.forEach((message, key) => {
        if (isSubscription(message)) {
            if (!subscribed.has(key)) {
                subscribed.add(key);
                frames.push(message);
            }
        } else {
            if (!state.requested.has(key)) frames.push(message);
            requested.add(key);
        }
    });
    return { frames, state: { subscribed, requested } };
}

/**
 * The identity of the subscription set, for an effect dependency: it changes when an instrument enters or
 * leaves the rows, and not when rows re-order, refresh their prices, or when the `liveData` array literal is
 * re-created on a render.
 */
export function subscriptionKey(rows: readonly unknown[], liveData: readonly { message: (row: any) => LiveMessage[] | null | undefined }[] | null | undefined): string {
    if (!liveData?.length || !rows.length) return "";
    return liveData.map((obj) => Array.from(collectMessages(rows, obj.message).keys()).sort().join(",")).join("|");
}
